import {
  AllowStringForNumbers,
  EditMode,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { useContextTS } from "@inrange/utils";
import { useEffect, useRef, useState } from "react";
import { Button, Col, Dropdown, Form, Modal, Row } from "react-bootstrap";
import { SiteCalculationsContext } from "../../../../../shared-components/src/SiteCalculationsContext";
import CollapsibleCard from "../CollapsibleCard";
import { getCurtailmentDetails } from "./dno-utils";
import { formatNumber, parseAsNumber } from "./utils";

const GridConnection = ({
  site,
  esys,
  setSite,
  editMode,
}: {
  site: SiteAllowStringValues;
  esys: AllowStringForNumbers<SiteElectricalSystem>;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  editMode: EditMode;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const isNL = site.countryCode === "NLD";
  const importMpanLabel = isNL ? "Connection EAN" : "Import MPAN";
  const exportMpanLabel = isNL ? "Production EAN" : "Export MPAN";
  const [reviewCurtailment, setReviewCurtailment] = useState(false);
  const [showCurtailmentModel, setShowCurtailmentModel] = useState(false);

  const prevCalculationsUpdatedAt = useRef<number | undefined>(undefined);
  const prevExportLimitWithZeroCurtailment = useRef<number | undefined>(undefined);

  const exportLimit = esys?.exportLimit;

  const energyFlowAnnual = siteCalculations.esys[esys.idx]?.energyFlowAnnual;

  useEffect(() => {
    const curtailed = energyFlowAnnual?.curtailed || 0;
    if (prevCalculationsUpdatedAt.current !== siteCalculations.updatedAt) {
      // Site calculations have changed
      prevCalculationsUpdatedAt.current = siteCalculations.updatedAt;
      if (reviewCurtailment && curtailed > 0) {
        // The user has taken an action that requires an optional curtailment review and
        // we've now seen some curtailment, so show the modal
        setShowCurtailmentModel(true);
      } else if (curtailed === 0) {
        // The user took an action requiring a review, and the latest calculation shows
        // zero curtailment so just update prevExportLimitWithZeroCurtailment
        prevExportLimitWithZeroCurtailment.current = parseAsNumber(exportLimit);
      }
      // We've seen a calculation update since the last time we marked a review as required
      // so reset this flag to false here
      setReviewCurtailment(false);
    }
  }, [
    reviewCurtailment,
    energyFlowAnnual,
    exportLimit,
    siteCalculations.updatedAt,
  ]);

  const [curtailment, curtailmentRisk, curtailmentColor] =
    getCurtailmentDetails(site, siteCalculations, esys);

  const hasExportLimit =
    site.esys[esys.idx]?.exportLimit !== "" &&
    site.esys[esys.idx]?.exportLimit !== null &&
    site.esys[esys.idx]?.exportLimit !== undefined;
  const hasHeadroom =
    siteCalculations.substationData?.success &&
    siteCalculations.substationData.headroom !== undefined;

  return (
    <CollapsibleCard
      title={`Grid connection${hasExportLimit ? ` - Export limit: ${site.esys[esys.idx]?.exportLimit} kVA` : ""}${hasHeadroom ? ` - Gen. headroom: ${Math.round((siteCalculations.substationData?.headroom ?? 0) * 1000)} kVA` : ""}`}
      className="mt-3"
      initialOpen={false}
      menu={
        editMode !== "SUMMARY" ? (
          <Dropdown>
            <Dropdown.Toggle
              variant="secondary"
              id="dropdown-basic"
              style={{ paddingTop: "2px", paddingBottom: "2px" }}
            >
              Actions
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item
                as="div"
                onClick={(e) => {
                  e.preventDefault();
                  navigator.clipboard.writeText(
                    `${site.id}${site.demand?.[esys.demandIdx]?.s3DemandSuffix || ""}`
                  );
                }}
              >
                Copy demand ID to clipboard
              </Dropdown.Item>
              <Dropdown.Item
                as="div"
                onClick={(e) => {
                  e.preventDefault();
                  navigator.clipboard.writeText(
                    `${site.esys?.[esys.idx]?.uuid || ""}`
                  );
                }}
              >
                Copy esys ID to clipboard
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ) : undefined
      }
    >
      <Row className="d-flex flex-wrap align-items-end">
        <Col sm={6}>
          <Form.Group className="mb-3" controlId={`importMPAN-${esys.idx}`}>
            <Form.Label>{importMpanLabel}</Form.Label>
            <Form.Control
              type="text"
              value={esys.importGridConnectionId ?? ""}
              onChange={(e) =>
                setSite({
                  esys: {
                    [esys.idx]: {
                      importGridConnectionId: e.target.value,
                    },
                  },
                })
              }
              isInvalid={!!errors[`esys.${esys.idx}.importGridConnectionId`]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`esys.${esys.idx}.importGridConnectionId`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={6}>
          <Form.Group
            className="mb-3"
            controlId={`importMeterNumber-${esys.idx}`}
          >
            <Form.Label>Import meter number</Form.Label>
            <Form.Control
              type="text"
              value={esys.importMeterId ?? ""}
              onChange={(e) =>
                setSite({
                  esys: {
                    [esys.idx]: {
                      importMeterId: e.target.value,
                    },
                  },
                })
              }
              isInvalid={!!errors[`esys.${esys.idx}.importMeterId`]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`esys.${esys.idx}.importMeterId`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={6}>
          <Form.Group className="mb-3" controlId={`exportMPAN-${esys.idx}`}>
            <Form.Label>{exportMpanLabel}</Form.Label>
            <Form.Control
              type="text"
              value={esys.exportGridConnectionId ?? ""}
              onChange={(e) =>
                setSite({
                  esys: {
                    [esys.idx]: {
                      exportGridConnectionId: e.target.value,
                    },
                  },
                })
              }
              isInvalid={!!errors[`esys.${esys.idx}.exportGridConnectionId`]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`esys.${esys.idx}.exportGridConnectionId`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={6}>
          <Form.Group
            className="mb-3"
            controlId={`exportMeterNumber-${esys.idx}`}
          >
            <Form.Label>Export meter number</Form.Label>
            <Form.Control
              type="text"
              value={esys.exportMeterId ?? ""}
              onChange={(e) =>
                setSite({
                  esys: {
                    [esys.idx]: {
                      exportMeterId: e.target.value,
                    },
                  },
                })
              }
              isInvalid={!!errors[`esys.${esys.idx}.exportMeterId`]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`esys.${esys.idx}.exportMeterId`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <Form.Group controlId={`exportLimit-${esys.idx}`}>
            <Form.Label>
              <div>
                <strong>Agreed export limit (kVA)</strong>
              </div>
              <div>Defining may cause curtailment.</div>
            </Form.Label>
            <Col sm={6}>
              <Form.Control
                type="text"
                value={esys.exportLimit ?? ""}
                onChange={(e) => {
                  if (
                    (siteCalculations.esys[esys.idx]?.energyFlowAnnual
                      ?.curtailed ?? 0) === 0
                  ) {
                    setReviewCurtailment(true);
                  }
                  setSite({
                    esys: {
                      [esys.idx]: {
                        exportLimit: e.target.value,
                      },
                    },
                  });
                  if (
                    parseAsNumber(site.esys[esys.idx]?.exportLimit) == 0 &&
                    parseAsNumber(e.target.value) > 0
                  ) {
                    setSite({
                      costInputsInrange: {
                        ...site.costInputsInrange!,
                        loanTermYears: 15,
                      },
                    });
                  } else if (
                    parseAsNumber(site.esys[esys.idx]?.exportLimit) > 0 &&
                    parseAsNumber(e.target.value) == 0
                  ) {
                    setSite({
                      costInputsInrange: {
                        ...site.costInputsInrange!,
                        loanTermYears: 10,
                      },
                    });
                  }
                }}
                isInvalid={!!errors[`esys.${esys.idx}.exportLimit`]}
                data-testid={`exportLimit-${esys.idx}`}
              />
              <Form.Control.Feedback type="invalid">
                {errors[`esys.${esys.idx}.exportLimit`]}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
        </Col>
        <Col sm={6}>
          <div>
            <strong>Generation headroom (kVA):</strong>{" "}
            {siteCalculations.substationData?.success
              ? siteCalculations.substationData.headroom === undefined
                ? "Headroom not defined"
                : Math.round(
                    (siteCalculations.substationData?.headroom ?? 0) * 1000
                  )
              : "Data unavailable"}
          </div>
          {curtailment === undefined && (
            <div>
              Curtailment risk:{" "}
              <strong style={{ color: curtailmentColor }}>
                {curtailmentRisk}
              </strong>{" "}
            </div>
          )}
        </Col>
      </Row>
      <Modal
        show={showCurtailmentModel}
        onHide={() => setShowCurtailmentModel(false)}
        centered
      >
        <Modal.Body>
          <span data-testid="export-warning">
            Applying this grid capacity will cause curtailment of{" "}
            {formatNumber(energyFlowAnnual?.curtailed ?? 0, 2, 2)} kWh per year.
            Are you sure you want to curtail?
          </span>
          <div
            style={{
              display: "flex",
              gap: 24,
              justifyContent: "center",
              marginTop: 8,
            }}
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowCurtailmentModel(false);
                setSite({
                  esys: {
                    [esys.idx]: {
                      exportLimit:
                        prevExportLimitWithZeroCurtailment.current || "",
                    },
                  },
                });
              }}
            >
              No, remove it
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                setShowCurtailmentModel(false);
              }}
            >
              Yes, continue
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </CollapsibleCard>
  );
};

export default GridConnection;
